import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Modal, Form, Select, message, Card, Typography, Tag, Layout } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/formatters';
import { contactService, Contact, CreateContactData } from '../services/contacts';

const { Title } = Typography;
const { Search } = Input;
const { Content } = Layout;

type ContactFilter = 'all' | 'customer' | 'supplier';

export const Contacts: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [filter, setFilter] = useState<ContactFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [form] = Form.useForm();

  // Set filter based on current path
  useEffect(() => {
    if (location.pathname === '/contacts/customers') {
      setFilter('customer');
    } else if (location.pathname === '/contacts/suppliers') {
      setFilter('supplier');
    } else {
      setFilter('all');
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchContacts();
  }, [filter, searchQuery]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filter !== 'all') {
        params.type = filter;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const response = await contactService.getAll(params);
      setContacts(response.data || []);
    } catch (error) {
      message.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingContact(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    form.setFieldsValue(contact);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await contactService.delete(id);
      message.success('Contact deleted successfully');
      fetchContacts();
    } catch (error) {
      message.error('Failed to delete contact');
    }
  };

  const handleSubmit = async (values: CreateContactData) => {
    try {
      if (editingContact) {
        await contactService.update(editingContact.id, values);
        message.success('Contact updated successfully');
      } else {
        await contactService.create(values);
        message.success('Contact created successfully');
      }
      setModalVisible(false);
      fetchContacts();
    } catch (error) {
      message.error(editingContact ? 'Failed to update contact' : 'Failed to create contact');
    }
  };

  const handleFilterChange = (value: ContactFilter) => {
    setFilter(value);
    if (value === 'all') {
      navigate('/contacts');
    } else if (value === 'customer') {
      navigate('/contacts/customers');
    } else if (value === 'supplier') {
      navigate('/contacts/suppliers');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Contact) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          {record.company_name && (
            <div style={{ fontSize: '12px', color: '#666' }}>{record.company_name}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'customer' ? 'blue' : 'green'}>
          {type === 'customer' ? 'Customer' : 'Supplier'}
        </Tag>
      ),
    },
    {
      title: 'Contact Person',
      dataIndex: 'contact_person',
      key: 'contact_person',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Credit Limit',
      dataIndex: 'credit_limit',
      key: 'credit_limit',
      render: (value: number) => formatCurrency(value, 'RM'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Contact) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Content style={{ padding: '24px' }}>
      <Title level={2}>Contacts</Title>
      
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Select
              value={filter}
              onChange={handleFilterChange}
              style={{ width: 120 }}
            >
              <Select.Option value="all">All</Select.Option>
              <Select.Option value="customer">Customers</Select.Option>
              <Select.Option value="supplier">Suppliers</Select.Option>
            </Select>
            
            <Search
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 300 }}
              onSearch={fetchContacts}
            />
          </div>
          
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Contact
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={contacts}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} contacts`,
          }}
        />
      </Card>

      <Modal
        title={editingContact ? 'Edit Contact' : 'Add Contact'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select type' }]}
          >
            <Select placeholder="Select type">
              <Select.Option value="customer">Customer</Select.Option>
              <Select.Option value="supplier">Supplier</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="contact_person"
            label="Contact Person"
          >
            <Input placeholder="Enter contact person" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
          >
            <Input placeholder="Enter phone" />
          </Form.Item>

          <Form.Item
            name="address"
            label="Address"
          >
            <Input.TextArea rows={3} placeholder="Enter address" />
          </Form.Item>

          <Form.Item
            name="company_name"
            label="Company Name"
          >
            <Input placeholder="Enter company name" />
          </Form.Item>

          <Form.Item
            name="tax_id"
            label="Tax ID"
          >
            <Input placeholder="Enter tax ID" />
          </Form.Item>

          <Form.Item
            name="credit_limit"
            label="Credit Limit"
          >
            <Input type="number" placeholder="Enter credit limit" />
          </Form.Item>

          <Form.Item
            name="payment_terms"
            label="Payment Terms"
          >
            <Input placeholder="Enter payment terms" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            initialValue="active"
          >
            <Select>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={3} placeholder="Enter notes" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingContact ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Content>
  );
};
